// src/store/slices/architectSlice.ts

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    ArchitectMessage,
    ArchitectPanelMode,
    UndoEntry,
    GraphDiff
} from '@/types/architect';

interface ArchitectState {
    // Panel state
    mode: ArchitectPanelMode;

    // Chat state
    messages: ArchitectMessage[];
    isProcessing: boolean;
    currentStatus: string | null;

    // Undo state
    undoStack: UndoEntry[];
    maxUndoSize: number;

    // Visual feedback
    highlightedNodes: GraphDiff | null;
    highlightExpiry: number | null;
}

const initialState: ArchitectState = {
    mode: 'components',
    messages: [],
    isProcessing: false,
    currentStatus: null,
    undoStack: [],
    maxUndoSize: 20,
    highlightedNodes: null,
    highlightExpiry: null,
};

const architectSlice = createSlice({
    name: 'architect',
    initialState,
    reducers: {
        // Panel mode
        setMode: (state, action: PayloadAction<ArchitectPanelMode>) => {
            state.mode = action.payload;
        },

        // Chat messages
        addMessage: (state, action: PayloadAction<ArchitectMessage>) => {
            state.messages.push(action.payload);
        },

        clearMessages: (state) => {
            state.messages = [];
        },

        // Processing state
        setProcessing: (state, action: PayloadAction<boolean>) => {
            state.isProcessing = action.payload;
            if (!action.payload) {
                state.currentStatus = null;
            }
        },

        setCurrentStatus: (state, action: PayloadAction<string | null>) => {
            state.currentStatus = action.payload;
        },

        // Undo stack
        pushToUndoStack: (state, action: PayloadAction<UndoEntry>) => {
            state.undoStack.push(action.payload);
            // Limit stack size
            if (state.undoStack.length > state.maxUndoSize) {
                state.undoStack.shift();
            }
        },

        popFromUndoStack: (state) => {
            state.undoStack.pop();
        },

        clearUndoStack: (state) => {
            state.undoStack = [];
        },

        // Visual feedback
        setHighlightedNodes: (state, action: PayloadAction<GraphDiff | null>) => {
            state.highlightedNodes = action.payload;
            state.highlightExpiry = action.payload ? Date.now() + 5000 : null;
        },

        clearHighlights: (state) => {
            state.highlightedNodes = null;
            state.highlightExpiry = null;
        },

        // Reset
        resetArchitect: () => initialState,
    },
});

export const {
    setMode,
    addMessage,
    clearMessages,
    setProcessing,
    setCurrentStatus,
    pushToUndoStack,
    popFromUndoStack,
    clearUndoStack,
    setHighlightedNodes,
    clearHighlights,
    resetArchitect,
} = architectSlice.actions;

export default architectSlice.reducer;
export type { ArchitectState };
